import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { Subscription, forkJoin, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { PromoPlanService } from '../promo-plan.service';
import { PromoPlanHeader } from '../promo-plan.model'; // Corrected import path
import { PromoPlanDetail } from '../promo-plan-detail.model';

// PrimeNG Modules
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { Chip, ChipModule } from 'primeng/chip';
import { TimelineModule } from 'primeng/timeline';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ApprovalCardComponent } from '../../chat-widgets/approval-card/approval-card.component';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { WorkflowApprovalService } from '../../chat-widgets/approval-card/workflow-approval.service';
import { HttpParams } from '@angular/common/http';
import { WorkflowApproval } from '../../chat-widgets/approval-card/workflow-apporval.model';
import { PromoPlanAttachment } from '../promo-plan-attachment.model';
import { AppUser } from '../../users/user.model';
import { AuthorizationService } from '../../services/authorization.service';
import * as XLSX from 'xlsx';
import { DialogModule } from "primeng/dialog";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TextareaModule } from 'primeng/textarea';
import { IftaLabelModule } from "primeng/iftalabel";
import { SelectModule } from 'primeng/select';
import { ItemService } from '../../master-data/items/item.service';
import { CustomerItemService } from '../../master-data/customer-item/customer-item.service';
import { DropdownModule } from "primeng/dropdown";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { filter, map, take } from 'rxjs/operators';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-view-promo-plan',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    ApprovalCardComponent,
    CardModule,
    TabsModule,
    TableModule,
    ToastModule,
    TagModule,
    ChipModule,
    ButtonModule,
    FileUploadModule,
    ProgressBarModule,
    TimelineModule,
    DividerModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    TooltipModule,
    DialogModule,
    NgxExtendedPdfViewerModule,
    FormsModule,
    InputNumberModule,
    InputSwitchModule,
    TextareaModule,
    SelectModule,
    IftaLabelModule,
    DropdownModule,
    AppDatePipe
  ],
  providers: [MessageService],
  templateUrl: './view-promo-plan.component.html',
  styleUrl: './view-promo-plan.component.css'
})
export class ViewPromoPlanComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;

  private routeSub!: Subscription;
  private sessionSub!: Subscription;
  currentSessionId: number | null = null;
  currentSession: ChatSession | undefined;

  maxRetries: number = 5;

  promoPlanHeader: PromoPlanHeader | null = null;
  promoPlanDetails: PromoPlanDetail[] = [];
  isLoading = false;
  promoPlanId: number | null = null;

  currentUser: AppUser | null = null;
  canGenerateSettlement: boolean = false;
  canUploadAttachment: boolean = false;
  canEditPromoPlanDetail: boolean = false;

  // Timeline stages
  timelineStages: any[] = [
    { status: 'Not Started', icon: 'pi pi-play' },
    { status: 'Under Execution', icon: 'pi pi-cog' },
    { status: 'Within 45 days', icon: 'pi pi-exclamation-triangle' },
    { status: 'More than 45 days', icon: 'pi pi-check' }
  ];
  currentTimelineStage: string = 'Not Started'; // Default timeline stage

  // Fields for global table filtering
  globalFilterFields: string[] = [
    'brand.brandName',
    'subBrand.subBrandname',
    'itemDescription',
    'tamerItemId.erpItemNameA',
    'customerItemId.customerItemDescription',
    'promoPlanHeader.channel'
  ];

  attachmentsChanged = false;
  isUploadingAttachments = false;
  attachmentUploadProgress = 0;

  pdfViewerVisible = false;
  pdfSrc: string | null = null;
  imageViewerVisible = false;
  officeViewerVisible = false;
  emlViewerVisible = false;
  imageSrc: string | null = null;
  officeSrc: SafeResourceUrl | null = null;
  emlSrc: SafeResourceUrl | null = null;


  items: any[] = [];
  filteredItems: any[] = [];
  customerItems: any[] = [];
  filteredCustomerItems: any[] = [];
  displayEditDialog = false;
  selectedDetail: PromoPlanDetail | null = null;
  originalSelectedDetail: PromoPlanDetail | null = null;

  // Cache for storing fetched pre-signed URLs to avoid redundant API calls.
  private attachmentUrlCache = new Map<number, { url: string; expires: number }>();
  private attachmentProgressByIndex = new Map<number, number>();


  constructor(
    private messageService: MessageService,
    private cd: ChangeDetectorRef,
    private promoPlanService: PromoPlanService,
    private route: ActivatedRoute,
    private sessionService: ChatSessionService,
    private router: Router, private itemService: ItemService,
    private customerItemService: CustomerItemService,
    private workflowApprovalService: WorkflowApprovalService,
    private chatSessionService: ChatSessionService,
    private authorizationService: AuthorizationService,
    private sanitizer: DomSanitizer,
    private storageService: FirebaseStorageService
  ) { }

  ngOnInit(): void {
    this.currentUser = JSON.parse(sessionStorage.getItem('appUser') ?? 'null');

    // The traversal logic is confirmed to be working. We can remove the verbose logging.
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (routeWithSessionId && !routeWithSessionId.snapshot.paramMap.has('sessionId')) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId');

      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionSub = this.chatSessionService.sessions$.subscribe(sessions => {
          this.currentSession = sessions.find(s => s.sessionId === this.currentSessionId);
        });
      } else {
        const errorMsg = 'Session ID not found in route parameters.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[ViewPromoPlanComponent] ERROR: ${errorMsg}`);
      }
    });

    let routeWithPromoPlanId: ActivatedRoute | null = this.route;
    while (routeWithPromoPlanId && !routeWithPromoPlanId.snapshot.paramMap.has('promoPlanId')) {
      routeWithPromoPlanId = routeWithPromoPlanId.parent;
    }

    this.routeSub = (routeWithPromoPlanId || this.route).paramMap.subscribe(params => {
      const promoPlanIdParam = params.get('promoPlanId');

      if (promoPlanIdParam) {
        this.promoPlanId = +promoPlanIdParam;
        this.loadPromoPlanData(this.promoPlanId);
      } else {
        const errorMsg = 'Promo Plan ID not found in route parameters. Cannot load data.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[ViewPromoPlanComponent] ERROR: ${errorMsg}`);
      }
    });
  }

  loadPromoPlanData(promoPlanId: number, retries: number = 0): void {
    this.isLoading = true;

    // Note: The backend doesn't seem to have an endpoint to get details by promoPlanId.
    // We fetch all details and filter client-side. This is inefficient for large datasets.
    // A dedicated backend endpoint would be a significant performance improvement.
    forkJoin({
      header: this.promoPlanService.getPromoPlanHeaderById(promoPlanId),
      details: this.promoPlanService.getPromoPlanDetailsByHeaderId(promoPlanId),
      approvals: this.workflowApprovalService.getAllHistory(new HttpParams({
        fromObject: {
          resourceTypeId: 1, // Assuming 1 is the resource type for PromoPlan
          businessObjectId: promoPlanId
        }
      }))
    }).subscribe({
      next: ({ header, details, approvals }) => {
        this.promoPlanHeader = header;
        this.promoPlanDetails = details;
        this.promoPlanHeader.workflowApprovals = approvals.content;
        void this.promoPlanService.rememberPromoPlan(header).subscribe();
        this.isLoading = false;
        this.checkAuthorization(); // Check authorization now that we have the promo plan data.
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Loaded promo plan ${promoPlanId}.` });

        // Set initial status and timeline stage based on loaded data (example logic)
        // In a real app, these would come from the backend or be derived from dates/other fields.
        if (this.promoPlanHeader) {
          // More refined timeline stage logic
          const today = new Date();
          const promoStartDate = new Date(this.promoPlanHeader.promoStartDate);
          const promoEndDate = new Date(this.promoPlanHeader.promoEndDate);
          const fortyFiveDaysAfterEnd = new Date(promoEndDate);
          fortyFiveDaysAfterEnd.setDate(promoEndDate.getDate() + 45);

          if (today < promoStartDate) {
            this.currentTimelineStage = 'Not Started';
          } else if (today >= promoStartDate && today <= promoEndDate) {
            this.currentTimelineStage = 'Under Execution';
          } else if (today > promoEndDate && today <= fortyFiveDaysAfterEnd) {
            this.currentTimelineStage = 'Within 45 days';
          } else if (today > fortyFiveDaysAfterEnd) {
            this.currentTimelineStage = 'More than 45 days';
          }
        }
      },
      error: err => {
        if (retries < this.maxRetries) {
          this.loadPromoPlanData(promoPlanId, retries + 1);
          return;
        }
        this.isLoading = false;
        this.promoPlanHeader = null;
        this.promoPlanDetails = [];
        this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: `Could not load data for promo plan ${promoPlanId}.` });
        console.error(`[ViewPromoPlanComponent] Error loading promo plan data for ID ${promoPlanId}:`, err);
      }
    });
  }

  private checkAuthorization(): void {
    this.canGenerateSettlement = this.authorizationService.hasPermission(this.currentUser, 'Settlement', ['create']);
    this.canUploadAttachment = this.authorizationService.hasPermission(this.currentUser, 'Promo Plan Attachment', ['create']);
    this.canEditPromoPlanDetail = this.authorizationService.hasPermission(this.currentUser, 'Promo Plan', ['update']);
  }

  isStageActive(stage: MenuItem): boolean {
    const currentIndex = this.timelineStages.indexOf(this.currentTimelineStage);
    const stageIndex = this.timelineStages.indexOf(stage);
    return stageIndex <= currentIndex;
  }

  getStatusSeverity(): string {
    switch (this.promoPlanHeader?.workflowStatus?.statusName) {
      case 'Active':
      case 'Settled':
        return 'success';
      case 'Changed':
      case 'Need Attention':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      case 'Draft':
      default:
        return 'info';
    }
  }

  formatFileSize(bytes: number | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * A wrapper method that efficiently gets a secure URL (from cache or server)
   * and then performs the desired action (view, download, or email).
   */
  private _executeAttachmentAction(attachment: PromoPlanAttachment, actionType: 'view' | 'download' | 'email', targetWindow?: Window | null): void {
    // Action: DOWNLOAD on a LOCAL file (not yet saved).
    // This can be handled immediately without a server call.
    if (actionType === 'download' && attachment.fileContent) {
      this.downloadLocalAttachment(attachment, targetWindow);
      return;
    }

    if (attachment.fileUrl && !attachment.attachmentId) {
      this._performActionWithUrl(actionType, attachment, attachment.fileUrl, targetWindow);
      return;
    }

    // For all other actions on saved files, we need a secure URL.
    if (!this.promoPlanId || !attachment.attachmentId) {
      const message = actionType === 'email'
        ? 'Please save new attachments before sharing them via email.'
        : 'This action is not available for unsaved attachments.';
      targetWindow?.close();
      this.messageService.add({ severity: 'warn', summary: 'Action Unavailable', detail: message });
      return;
    }

    // Check cache for a valid, non-expired URL.
    const cachedUrl = this.attachmentUrlCache.get(attachment.attachmentId);
    if (cachedUrl && cachedUrl.expires > Date.now()) {
      this._performActionWithUrl(actionType, attachment, cachedUrl.url, targetWindow);
      return;
    }

    // If not in cache or expired, fetch a new URL from the server.
    this.messageService.add({ severity: 'info', summary: 'Loading', detail: 'Generating secure link...', life: 2000 });
    this.promoPlanService.getAttachmentViewUrl(this.promoPlanId, attachment.attachmentId).subscribe({
      next: (response) => {
        if (response && response.url) {
          // Cache the new URL for 5 minutes.
          const expirationTime = Date.now() + 5 * 60 * 1000;
          this.attachmentUrlCache.set(attachment.attachmentId!, { url: response.url, expires: expirationTime });
          this._performActionWithUrl(actionType, attachment, response.url, targetWindow);
        } else {
          targetWindow?.close();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve a valid URL.' });
        }
      },
      error: (err) => {
        console.error(`Error getting attachment URL for ${actionType}:`, err);
        targetWindow?.close();
        this.messageService.add({ severity: 'error', summary: 'Access Denied', detail: 'Could not get permission for the file.' });
      }
    });
  }

  /** Helper to execute the final action once a secure URL is obtained. */
  private _performActionWithUrl(actionType: 'view' | 'download' | 'email', attachment: PromoPlanAttachment, url: string, targetWindow?: Window | null): void {
    switch (actionType) {
      case 'view':
        const fileName = (attachment.fileName || '').toLowerCase();
        if (fileName.endsWith('.pdf')) {
          this.pdfSrc = url;
          this.pdfViewerVisible = true;
        } else if (fileName.match(/\.(jpeg|jpg|gif|png|svg|webp)$/)) {
          this.imageSrc = url;
          this.imageViewerVisible = true;
        } else if (fileName.match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/)) {
          const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
          this.officeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(officeViewerUrl);
          this.officeViewerVisible = true;
        } else if (fileName.endsWith('.eml')) {
          this.emlSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          this.emlViewerVisible = true;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'download':
        this.openUrlInAttachmentTab(url, targetWindow);
        break;
      case 'email':
        const subject = `Attachment: ${attachment.fileName} from Promo Plan #${this.promoPlanHeader?.promoPlanId}`;
        let body = `Please find the attached file: ${attachment.fileName}.`;
        body += `\n\nYou can view or download it from this secure link: ${url}`;
        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
        break;
    }
  }

  viewAttachment(attachment: PromoPlanAttachment): void {
    this._executeAttachmentAction(attachment, 'view');
  }

  private openBlankAttachmentTab(): Window | null {
    const targetWindow = window.open('about:blank', '_blank');
    if (!targetWindow) {
      this.messageService.add({ severity: 'warn', summary: 'Popup Blocked', detail: 'Please allow popups to download attachments in a new tab.' });
    }
    return targetWindow;
  }

  private openUrlInAttachmentTab(url: string, targetWindow?: Window | null): void {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.opener = null;
      targetWindow.location.href = url;
      return;
    }

    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      this.messageService.add({ severity: 'warn', summary: 'Popup Blocked', detail: 'Please allow popups to open attachments in a new tab.' });
    }
  }

  downloadAttachment(attachment: PromoPlanAttachment): void {
    const targetWindow = this.openBlankAttachmentTab();
    if (!targetWindow) {
      return;
    }
    this._executeAttachmentAction(attachment, 'download', targetWindow);
  }

  emailAttachment(attachment: PromoPlanAttachment): void {
    this._executeAttachmentAction(attachment, 'email');
  }

  /** Helper to download a new, unsaved file directly from its base64 content. */
  private downloadLocalAttachment(attachment: PromoPlanAttachment, targetWindow?: Window | null): void {
    if (!attachment.fileContent) return;
    try {
      const base64Response = attachment.fileContent.split(',')[1];
      const byteCharacters = atob(base64Response);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.fileType });
      const objectUrl = window.URL.createObjectURL(blob);

      if (targetWindow && !targetWindow.closed) {
        targetWindow.opener = null;
        targetWindow.location.href = objectUrl;
        return;
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.fileName;
      link.click();
    } catch (e) {
      console.error('Error decoding or downloading base64 file:', e);
      this.messageService.add({ severity: 'error', summary: 'Download Failed', detail: 'Could not process the file for download.' });
    }
  }

  onFileSelect(event: any): void {
    if (!this.promoPlanHeader) return;
    if (!this.promoPlanHeader.attachments) {
      this.promoPlanHeader.attachments = [];
    }

    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();
    const promoPlanFolder = `promo-plan/${this.promoPlanHeader.promoPlanId ?? 'draft'}`;

    forkJoin(
      files.map((file, index) => {
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${promoPlanFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(file, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, files.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileUrl: event.downloadURL!,
            uploadedBy: this.currentUser?.fullName || this.currentUser?.email || 'N/A',
            uploadedDate: new Date()
          } as PromoPlanAttachment))
        );
      })
    ).subscribe({
      next: (uploadedAttachments) => {
        this.promoPlanHeader!.attachments.push(...uploadedAttachments);
        this.attachmentsChanged = true;
        this.isUploadingAttachments = false;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.isUploadingAttachments = false;
        this.attachmentUploadProgress = 0;
        console.error('Error uploading attachment to Firebase Storage:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: 'Could not upload the selected file(s).'
        });
      }
    });
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  removeAttachment(index: number): void {
    if (this.promoPlanHeader && this.promoPlanHeader.attachments) {
      this.promoPlanHeader.attachments.splice(index, 1);
      this.attachmentsChanged = true;
    }
  }

  saveAttachments(): void {
    if (!this.promoPlanHeader || !this.promoPlanHeader.promoPlanId || !this.promoPlanHeader.attachments) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'No promo plan data available to save attachments.' });
      return;
    }

    // Filter for only the newly added attachments.
    const newAttachments = this.promoPlanHeader.attachments.filter(att => !att.attachmentId && (att.fileUrl || att.fileContent));

    if (newAttachments.length === 0) {
      this.attachmentsChanged = false; // Reset flag as there's nothing new to save.
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No new attachments to upload.' });
      return;
    }

    this.isLoading = true;
    this.promoPlanService.uploadAttachments(this.promoPlanHeader.promoPlanId, newAttachments).subscribe({
      next: (savedAttachments) => {
        this.isLoading = false;
        this.attachmentsChanged = false;
        // Replace the temporary attachments with the saved ones from the server.
        this.promoPlanHeader!.attachments = this.promoPlanHeader!.attachments.filter(att => att.attachmentId);
        this.promoPlanHeader!.attachments.push(...savedAttachments);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attachments saved successfully!' });
        this.cd.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save attachments.' });
        console.error('Error saving attachments:', err);
      }
    });
  }

  onStatusChange(status: WorkflowApproval) {
    this.promoPlanHeader!.workflowStatus = status.workflowStatus;
  }

  generateNewSettlement(): void {
    if (this.promoPlanId && this.currentSessionId !== null) {
      // Use an absolute path within the named outlet, preserving the current session
      this.router.navigate(
        [
          '/ai-studio/session',
          this.currentSessionId,
          { outlets: { canvasOutlet: ['settlement', 'add'] } },
        ],
        {
          queryParams: { promoPlanId: this.promoPlanId },
        }
      );
    } else {
      this.messageService.add({
        severity: 'warn', summary: 'Navigation Error', detail: 'Cannot generate settlement: Missing Promo Plan ID or Session ID.'
      });
    }
  }

  openSession() {
    if (this.promoPlanHeader?.linkedSessionId) {
      this.router.navigate([
        '/ai-studio/session',
        this.promoPlanHeader.linkedSessionId,
        {
          outlets: {
            canvasOutlet: ['promo-plan', 'view', this.promoPlanHeader.promoPlanId],
          },
        },
      ]);
    } else {
      this.messageService.add({ severity: 'warn', summary: 'No Session', detail: 'No active session to open.' });
    }
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
  }

  exportDetailsToExcel(): void {
    if (!this.promoPlanDetails || this.promoPlanDetails.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There are no details to export.' });
      return;
    }

    const dataToExport = this.promoPlanDetails.map(detail => ({
      'NAME': this.promoPlanHeader?.customer.customerName,
      'CHANNEL': this.promoPlanHeader?.channel,
      'CUSTOMER NAME': this.promoPlanHeader?.customer.customerName,
      'CUSTOMER NUMBER': this.promoPlanHeader?.customer.customerNumber,
      'BRAND': detail.brand?.brandName,
      'SUB-BRAND': detail.subBrand?.subBrandName,
      'TAMER ITEM No.': detail.tamerItem?.erpItemNumber ?? 'Assorted',
      'CUSTOMER ITEM No.': detail.customerItem?.customerItemNumber ?? 'Assorted',
      'DESCRIPTION': detail.itemDescription,
      'MONTH': this.promoPlanHeader?.monthYear,
      'PROMO START DATE': this.promoPlanHeader?.promoStartDate ? new Intl.DateTimeFormat('en-GB').format(new Date(this.promoPlanHeader.promoStartDate)) : '',
      'PROMO END DATE': this.promoPlanHeader?.promoEndDate ? new Intl.DateTimeFormat('en-GB').format(new Date(this.promoPlanHeader.promoEndDate)) : '',
      'PROMO TYPE': this.promoPlanHeader?.activityType?.name,
      'EXECUTION TYPE': this.promoPlanHeader?.executionType,
      'CITY': this.promoPlanHeader?.city,
      'IMS': detail.ims,
      'PTT': detail.ptt,
      'PROMO DESCRIPTION': this.promoPlanHeader?.promoDescription,
      'DISCOUNT %': detail.discountPercentage,
      'PRICE AFTER DISCOUNT': detail.priceAfterDiscount,
      'COMPENSATION PER UNIT OF SELLOUT': detail.compensationPerUnitOfSellout,
      'ESTIMATED VOLUME (BASED ON HISTORICAL DATA)': detail.estimatedVolume,
      'TOTAL COMPENSATION/FIXED AMOUNT': detail.totalCompensationFixedAmount,
      'CURRENT COST': detail.compensationBasedOnLc,
      'STOCK OH': detail.stockOh,
      'CURRENT RSP': detail.currentRsp,
      'PROMO RRP': detail.promoRrp,
      'NEW COST': detail.newCost,
      'COMMENTS': detail.comments
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PromoPlanDetails');
    XLSX.writeFile(wb, `PromoPlan_${this.promoPlanId}_Details.xlsx`);
  }

  editDetail(detail: PromoPlanDetail): void {
    this.originalSelectedDetail = { ...detail }; // Store the original detail
    this.selectedDetail = { ...detail }; // Create a copy for editing
    this.loadItems(); // Load items for the dropdown
    this.loadCustomerItems(); // Load customer items for the dropdown
    this.displayEditDialog = true;
  }

  saveDetail(): void {
    if (this.selectedDetail && this.originalSelectedDetail) {
      const changedDate = new Date().toISOString();
      this.selectedDetail.promoPlanLineId = undefined;
      this.selectedDetail.originalPromoPlanLineId = this.originalSelectedDetail.promoPlanLineId;
      this.selectedDetail.isActive = true;
      this.selectedDetail.changedDate = changedDate;
      // Step 1: Create the new promo plan detail
      this.promoPlanService.createPromoPlanDetail(this.selectedDetail, this.promoPlanId!).pipe(
        // Step 2: After successful creation, update the original detail
        concatMap(() => {
          this.originalSelectedDetail!.isActive = false; // Mark original as inactive
          this.originalSelectedDetail!.changedDate = changedDate; // Set changed date for original
          return this.promoPlanService.updatePromoPlanDetail(this.originalSelectedDetail!.promoPlanLineId!, this.originalSelectedDetail!, this.promoPlanId!); // Update original
        })
      ).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Promo plan detail updated successfully.' });
          this.displayEditDialog = false;
          this.loadPromoPlanData(this.promoPlanId!); // Refresh data
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update promo plan detail.' });
          console.error('Error updating promo plan detail:', err);
        }
      });
    }
  }

  cancelEdit(): void {
    this.displayEditDialog = false;
    this.selectedDetail = null;
  }

  loadItems(): void {
    this.itemService.getAllItems({ page: 0, size: 10000 }).subscribe({
      next: (page) => {
        this.items = page.content;
        this.filteredItems = this.items;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load items.' });
        console.error('Error loading items:', err);
      }
    });
  }

  filterItems(query: string) {
    if (!query) { // Check for null, undefined, or empty string
      this.filteredItems = this.items;
    } else {
      this.filteredItems = this.items.filter(item =>
        item.erpItemNameE.toLowerCase().includes(query.toLowerCase())
        || item.erpItemNumber.toLowerCase().includes(query.toLowerCase())
      );
    }
  }

  loadCustomerItems(): void {
    this.customerItemService.findAll({ page: 0, size: 10000 }).subscribe({
      next: (page) => {
        this.customerItems = page.content;
        this.filteredCustomerItems = this.customerItems;
        if (this.selectedDetail?.tamerItem) {
          this.onTamerItemChange({ value: this.selectedDetail.tamerItem });
        }
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load customer items.' });
        console.error('Error loading customer items:', err);
      }
    });
  }

  filterCustomerItems(query: string) {
    if (!query) { // Check for null, undefined, or empty string
      this.filteredCustomerItems = this.customerItems;
    } else {
      this.filteredCustomerItems = this.customerItems.filter(item =>
        item.customerItemDescription.toLowerCase().includes(query.toLowerCase())
        || item.customerItemNumber.toLowerCase().includes(query.toLowerCase())
      );
    }
  }

  onTamerItemChange(event: any): void {
    const selectedTamerItem = event.value;
    if (selectedTamerItem && selectedTamerItem.itemId) {
      this.filteredCustomerItems = this.customerItems.filter(
        (ci) => ci.tamerItem?.itemId === selectedTamerItem.itemId
      );
    } else {
      this.filteredCustomerItems = [];
    }
    // Clear selected customer item if it no longer matches the filtered list
    if (this.selectedDetail && !this.filteredCustomerItems.some(fci => fci.customerItemId === this.selectedDetail?.customerItem?.customerItemId)) {
      this.selectedDetail.customerItem = undefined;
    }
  }
}
